import { useMemo } from 'react'
import _ from 'lodash'
import type { ApolloError } from '@apollo/client'
import {
  FilterInput,
  GetCategoryTrendsQuery,
  useGetCategoryTrendsQuery,
} from '../../../graphql/generated'
import {
  DataPoint,
  fillYearGaps,
  FilledDataPoint,
  makeTrendLine,
} from '../../../utils/chart'

/**
 * Line chart data for a single category.
 *
 * Contains data points for drawing a chart, and data points
 * for a trend line over the same X-domain as the chart points.
 *
 * The X-range for both the chart- and trend points is "gap-filled";
 * it contains all values in the range [min, max] over the x-values
 * found in the emissions.
 */
export type LineChartData = GetCategoryTrendsQuery['getCategoryTrends'][0] & {
  emissionPoints: FilledDataPoint[]
  trendPoints: DataPoint[]
}

/**
 * Line chart response. If successful, it contains a list of chart data for
 * one or more category line charts.
 */
export type LineChartInfo = {
  loading: boolean
  error: ApolloError | undefined
  categoryTrends: LineChartData[]
}

export const useCategoryLineCharts = (filter: FilterInput): LineChartInfo => {
  // Note: the category trend query always uses Year as the period,
  //       which is an important assumtion for `fillYearGaps`,
  //       as it expects x:s to be years (actually any integer series).
  const { data, loading, error, previousData } = useGetCategoryTrendsQuery({
    variables: {
      filter,
    },
  })

  const lineChartData = useMemo<LineChartData[]>(() => {
    const catData =
      (loading ? previousData?.getCategoryTrends : data?.getCategoryTrends) ??
      []
    return catData.map<LineChartData>((cd) => {
      const sortedPoints = _(cd.emissions)
        .map((e) => ({
          x: e.period,
          y: e.totalCO2e,
        }))
        .sortBy('x')
        .value()

      const filledPoints = fillYearGaps(sortedPoints)

      const trendPoints = makeTrendLine({
        sortedXs: filledPoints.map((p) => p.x),
        trendOffset: cd.trendOffset,
        trendRate: cd.trendRate,
      })

      return {
        ...cd,
        emissionPoints: filledPoints,
        trendPoints,
      }
    })
  }, [data?.getCategoryTrends, loading, previousData?.getCategoryTrends])

  return {
    loading,
    error,
    categoryTrends: lineChartData,
  }
}
